import type { GetMemberUsageResponseBody } from "@app/lib/api/credits/members_usage";
import type { GetCreditPurchaseInfoResponseBody } from "@app/lib/api/credits/purchase";
import type { SeatPlanResponseBody } from "@app/lib/api/credits/seat_plan";
import type { GetAwuPurchaseInfoResponseBody } from "@app/lib/credits/awu_purchase";
import type { GetAwuPurchaseStatusResponseBody } from "@app/lib/credits/awu_purchase_status";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type {
  AwuPoolCurrentCycleResponseBody,
  AwuPoolCycleBreakdown,
  AwuPoolCycleHistoryOverflow,
  AwuPoolCycleHistoryResponseBody,
  AwuPoolSummaryResponseBody,
} from "@app/types/api/credits/awu_pool_summary";
import type { GetMembersSeatsResponseBody } from "@app/types/api/credits/members_seats";
import type { GetAwuTopUpsHistoryResponseBody } from "@app/types/api/credits/top_ups_history";
import type {
  GetCreditsResponseBody,
  PendingCreditData,
} from "@app/types/credits";
import { useCallback, useSyncExternalStore } from "react";
import type { Fetcher } from "swr";

// Global state for tracking purchase loading status per workspace
const purchaseLoadingState = new Map<string, boolean>();

const EMPTY_HAS_MORE_CYCLE_HISTORY: AwuPoolCycleHistoryOverflow = Object.freeze(
  { cycleBreakdown: false, excessCycleBreakdown: false }
);
const purchaseLoadingListeners = new Set<() => void>();

function setPurchaseLoading(workspaceId: string, loading: boolean) {
  purchaseLoadingState.set(workspaceId, loading);
  purchaseLoadingListeners.forEach((listener) => listener());
}

function getPurchaseLoading(workspaceId: string): boolean {
  return purchaseLoadingState.get(workspaceId) ?? false;
}

function subscribeToPurchaseLoading(callback: () => void) {
  purchaseLoadingListeners.add(callback);
  return () => {
    purchaseLoadingListeners.delete(callback);
  };
}

// Global state for tracking post-purchase refresh count per workspace
const postPurchaseRefreshState = new Map<string, number>();
const postPurchaseRefreshListeners = new Set<() => void>();

function getPostPurchaseRefreshCount(workspaceId: string): number {
  return postPurchaseRefreshState.get(workspaceId) ?? Infinity;
}

function incrementPostPurchaseRefreshCount(workspaceId: string): void {
  const current = postPurchaseRefreshState.get(workspaceId) ?? Infinity;
  if (current < 5) {
    postPurchaseRefreshState.set(workspaceId, current + 1);
    postPurchaseRefreshListeners.forEach((listener) => listener());
  }
}

function resetPostPurchaseRefreshCount(workspaceId: string): void {
  postPurchaseRefreshState.set(workspaceId, 0);
  postPurchaseRefreshListeners.forEach((listener) => listener());
}

export function useCredits({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const creditsFetcher: Fetcher<GetCreditsResponseBody> = fetcher;

  const { data, error, mutate, isValidating } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits`,
    creditsFetcher,
    {
      disabled,
      refreshInterval: () => {
        const count = getPostPurchaseRefreshCount(workspaceId);
        if (count < 5) {
          incrementPostPurchaseRefreshCount(workspaceId);
          return 5000;
        }
        return 0;
      },
    }
  );

  const pendingCredits: PendingCreditData[] = data?.pendingCredits ?? [];

  return {
    credits: data?.credits ?? emptyArray(),
    pendingCredits,
    freeCreditRenewalDateMs: data?.freeCreditRenewalDateMs ?? null,
    isCreditsLoading: !error && !data && !disabled,
    isCreditsValidating: isValidating,
    isCreditsError: error,
    mutateCredits: mutate,
  };
}

type PurchaseResult =
  | { status: "success" }
  | { status: "redirect"; paymentUrl: string }
  // Formatted by the caller with `formatError`.
  | { status: "error"; error: unknown };

export function usePurchaseCredits({ workspaceId }: { workspaceId: string }) {
  const isLoading = useSyncExternalStore(
    subscribeToPurchaseLoading,
    () => getPurchaseLoading(workspaceId),
    () => false
  );

  const { mutateCredits } = useCredits({
    disabled: true,
    workspaceId,
  });

  const purchaseCredits = useCallback(
    async (amountDollars: number): Promise<PurchaseResult> => {
      if (getPurchaseLoading(workspaceId)) {
        return {
          status: "error",
          error: new Error("Purchase already in progress"),
        };
      }

      setPurchaseLoading(workspaceId, true);

      try {
        const response = await clientFetch(
          `/api/w/${workspaceId}/credits/purchase`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ amountDollars }),
          }
        );

        if (!response.ok) {
          const errorData: unknown = await response.json();
          return { status: "error", error: errorData };
        }

        const responseData = await response.json();

        if (responseData.paymentUrl) {
          return { status: "redirect", paymentUrl: responseData.paymentUrl };
        }

        resetPostPurchaseRefreshCount(workspaceId);
        void mutateCredits();

        return { status: "success" };
      } catch (err) {
        return { status: "error", error: err };
      } finally {
        setPurchaseLoading(workspaceId, false);
      }
    },
    [workspaceId, mutateCredits]
  );

  return {
    purchaseCredits,
    isLoading,
  };
}

export function useCreditPurchaseInfo({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const creditPurchaseInfoFetcher: Fetcher<GetCreditPurchaseInfoResponseBody> =
    fetcher;

  const { data, error, isValidating } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/purchase`,
    creditPurchaseInfoFetcher,
    {
      disabled,
    }
  );

  return {
    isEnterprise: data?.isEnterprise ?? false,
    currency: data?.currency ?? "usd",
    discountPercent: data?.discountPercent ?? 0,
    creditPricing: data?.creditPricing ?? null,
    creditPurchaseLimits: data?.creditPurchaseLimits ?? null,
    billingCycleStartDay: data?.billingCycleStartDay ?? null,
    isCreditPurchaseInfoLoading: !error && !data && !disabled,
    isCreditPurchaseInfoValidating: isValidating,
    isCreditPurchaseInfoError: error,
  };
}

export function useAwuPoolSummary({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const awuFetcher: Fetcher<AwuPoolSummaryResponseBody> = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/awu-pool-summary`,
    awuFetcher,
    { disabled }
  );

  return {
    totalRemainingCredits: data?.totalRemainingCredits ?? 0,
    totalActiveCredits: data?.totalActiveCredits ?? 0,
    overageCredits: data?.overageCredits ?? null,
    overageAmountCents: data?.overageAmountCents ?? null,
    overageCurrency: data?.overageCurrency ?? null,
    isAwuPoolSummaryLoading: !error && !data && !disabled,
    isAwuPoolSummaryError: error,
    isAwuPoolSummaryValidating: isValidating,
    mutateAwuPoolSummary: mutate,
  };
}

export function useAwuPoolCurrentCycle({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const awuFetcher: Fetcher<AwuPoolCurrentCycleResponseBody> = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/awu-pool-current-cycle`,
    awuFetcher,
    { disabled }
  );

  return {
    awuPoolCurrentCycle: data ?? null,
    isAwuPoolCurrentCycleLoading: !error && !data && !disabled,
    isAwuPoolCurrentCycleError: error,
    isAwuPoolCurrentCycleValidating: isValidating,
    mutateAwuPoolCurrentCycle: mutate,
  };
}

export function useAwuPoolCycleHistory({
  workspaceId,
  cycleHistoryLimit,
  disabled,
}: {
  workspaceId: string;
  cycleHistoryLimit?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const awuFetcher: Fetcher<AwuPoolCycleHistoryResponseBody> = fetcher;

  const { data, error, isValidating, mutateRegardlessOfQueryParams } =
    useSWRWithDefaults(
      `/api/w/${workspaceId}/credits/awu-pool-cycle-history${
        cycleHistoryLimit ? `?cycleHistoryLimit=${cycleHistoryLimit}` : ""
      }`,
      awuFetcher,
      // Keep rows on screen while a larger limit is fetched so "Load more"
      // appends instead of swapping the table for a spinner.
      { disabled, keepPreviousData: true }
    );

  const isUsable = !error && !disabled;

  return {
    cycleBreakdown: isUsable
      ? (data?.cycleBreakdown ?? emptyArray<AwuPoolCycleBreakdown>())
      : emptyArray<AwuPoolCycleBreakdown>(),
    excessCycleBreakdown: isUsable
      ? (data?.excessCycleBreakdown ?? emptyArray<AwuPoolCycleBreakdown>())
      : emptyArray<AwuPoolCycleBreakdown>(),
    hasMoreCycleHistoryByBreakdown: isUsable
      ? (data?.hasMoreCycleHistoryByBreakdown ?? EMPTY_HAS_MORE_CYCLE_HISTORY)
      : EMPTY_HAS_MORE_CYCLE_HISTORY,
    isAwuPoolCycleHistoryLoading: !error && !data && !disabled,
    isAwuPoolCycleHistoryError: error,
    isAwuPoolCycleHistoryValidating: isValidating,
    mutateAwuPoolCycleHistory: mutateRegardlessOfQueryParams,
  };
}

export function useAwuTopUpsHistory({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const topUpsFetcher: Fetcher<GetAwuTopUpsHistoryResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/top-ups`,
    topUpsFetcher,
    { disabled }
  );

  return {
    topUps: data?.topUps ?? emptyArray(),
    isTopUpsHistoryLoading: !error && !data && !disabled,
    isTopUpsHistoryError: error,
    mutateTopUpsHistory: mutate,
  };
}

// Polls the latest AWU purchase attempt status for a workspace. The dialog
// drives `disabled` off its own "processing" state so we only poll while a
// purchase is in flight.
export function useAwuPurchaseStatus({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const statusFetcher: Fetcher<GetAwuPurchaseStatusResponseBody> = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/subscriptions/awu-purchase-status`,
    statusFetcher,
    {
      disabled,
      refreshInterval: (latest) => {
        if (!latest?.attempt) {
          return 2000;
        }
        return latest.attempt.status === "pending" ? 2000 : 0;
      },
    }
  );

  return {
    attempt: data?.attempt ?? null,
    isAwuPurchaseStatusLoading: !error && !data && !disabled,
    isAwuPurchaseStatusValidating: isValidating,
    isAwuPurchaseStatusError: error,
    mutateAwuPurchaseStatus: mutate,
  };
}

export function useAwuPurchaseInfo({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const awuPurchaseInfoFetcher: Fetcher<GetAwuPurchaseInfoResponseBody> =
    fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/subscriptions/awu-purchase`,
    awuPurchaseInfoFetcher,
    { disabled }
  );

  return {
    awuPurchaseInfo: data ?? null,
    isAwuPurchaseInfoLoading: !error && !data && !disabled,
    isAwuPurchaseInfoValidating: isValidating,
    isAwuPurchaseInfoError: error,
    mutateAwuPurchaseInfo: mutate,
  };
}

type RedeemPoolTopupCouponOutcome =
  | { status: "success" }
  // Formatted by the caller with `formatError`.
  | { status: "error"; error: unknown };

// Redeems a "credits" coupon (the "Use coupon" Top-Up tab). Grants free AWU
// credits synchronously — no payment involved. Refreshes the pool summary on
// success.
export function useRedeemPoolTopupCoupon({
  workspaceId,
}: {
  workspaceId: string;
}) {
  const { mutateAwuPoolSummary } = useAwuPoolSummary({
    workspaceId,
    disabled: true,
  });

  const redeemPoolTopupCoupon = useCallback(
    async (code: string): Promise<RedeemPoolTopupCouponOutcome> => {
      try {
        const response = await clientFetch(
          `/api/w/${workspaceId}/coupon/redeem`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          }
        );

        if (!response.ok) {
          const errorData: unknown = await response.json().catch(() => null);
          return { status: "error", error: errorData };
        }

        void mutateAwuPoolSummary();

        return { status: "success" };
      } catch (err) {
        return { status: "error", error: err };
      }
    },
    [workspaceId, mutateAwuPoolSummary]
  );

  return { redeemPoolTopupCoupon };
}

export function useMyUsage({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const myUsageFetcher: Fetcher<GetMemberUsageResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/my-usage`,
    myUsageFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60_000,
      disabled,
    }
  );

  return {
    myUsage: data?.member ?? null,
    premiumModelUsage: data?.premiumModelUsage ?? null,
    creditUsageStatus: data?.creditUsageStatus ?? null,
    nextCreditResetAt: data?.member?.nextCreditResetAt ?? null,
    isMyUsageLoading: !error && !data && !disabled,
    isMyUsageError: error,
  };
}

const EMPTY_SEAT_PLANS: SeatPlanResponseBody = {};

export function useMembersSeats({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const membersSeatsFetcher: Fetcher<GetMembersSeatsResponseBody> = fetcher;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/credits/members-seats`,
    membersSeatsFetcher,
    { disabled }
  );

  return {
    membersSeats: data?.seatTypes ?? {},
    metronomeSeats: data?.metronomeSeats ?? {},
    totalMembersSeats: data?.total ?? 0,
    isMembersSeatsLoading: !error && !data && !disabled,
    isMembersSeatsError: error,
    isMembersSeatsValidating: isValidating,
    mutateMembersSeats: mutate,
  };
}

export function useSeatPlan({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const seatPlanFetcher: Fetcher<SeatPlanResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    `/api/w/${workspaceId}/seats/plan`,
    seatPlanFetcher,
    { disabled }
  );

  return {
    seatPlans: data ?? EMPTY_SEAT_PLANS,
    isSeatPlanLoading: !error && !data && !disabled,
    isSeatPlanError: error,
  };
}
